// noinspection GraphQLUnresolvedReference

/*
 * Copyright (c) 2023. RoZetta Technology Pty Ltd. All rights reserved.
 */

import * as React from "react";
import {
  Alert,
  Button,
  Grid,
  Box,
  Typography,
  Tooltip,
  ToggleButtonGroup,
  LinearProgress,
  Slider,
  Tab,
  Tabs,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../../services/utils/HttpUtils";
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Title,
	Tooltip as ChartTooltip,
	Legend,
	LineController,
	BarController,
	Colors
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useRef, useEffect, useState } from "react";
import {
  GetOverallResult,
  GetDetailedResult,
  PollScenarioModelProgress,
  GetDetailedProjectResult
} from "../../graphql/queries_scenarioOutput";
import {
	overallTrendOptions,
	filterConfigList
} from "./componentsConfig/scenarioOutputConfig";
import PlotFilterList from "../utils/plotFilterList";
// @ts-ignore
import dayjs from "dayjs";
import { colorPelletList, scenarioResultMessage, scenarioModelRunStatusMessage } from "./TableData";
import formatQueryParams from "../../services/utils/formatQueryParams";
import { useMsal } from "@azure/msal-react";
import * as Excel from "exceljs/dist/exceljs.min.js";
import { saveAs } from "file-saver";
import { getAllLists } from "../../graphql/queries_pipelineEditor";
import {getUserInfo} from "../../graphql/queries_user_info";
import ProcessExecutionDialog, {removeDuplicate} from "./util/displayUtils";
import ExportDialogs from "./ExportDialogs";

ChartJS.register(
	Colors,
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	Title,
	ChartTooltip,
	Legend,
	LineController,
	BarController
);

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;


	return (
		<Box
			role="tabpanel"
			className='tabPanelBox'
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
			sx={{ border: 2 }}
		>
			{value === index && (
				<Box className='tabBox' sx={{ p: 1 }}>
					{children}
				</Box>
			)}
		</Box>
	);
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

export default function ScenarioOutput(props: any) {

	const { setTabIndex, scenarioID, modelFlag } = props;
	const client = useQueryClient();
  const {data: userInfo} = getUserInfo();
	function handleUpdateTab(index: number) {
		setTabIndex(index);
	}
	const [dashTabIndex, setDashTabIndex] = useState(0);
	const [pollScenarioProgress, setPollScenarioProgress] = useState({
		isProcessing: true,
		scenarioModelRunStatus: scenarioModelRunStatusMessage,
	});
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [totalMonths, setTotalMonths] = useState(0);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [showExportWarning, setShowExportWarning] = useState(false);
  const [showExportConfirmation, setShowExportConfirmation] = useState(false);
  const [plotRange, setPlotRange] = useState<number[]>([new Date().getFullYear(), new Date().getFullYear() + 16]);
  const handleDashTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDashTabIndex(newValue);
  };

	// Fix the issue pollProgress is called before the runModel
	useEffect(() => {
		const timer = setTimeout(() => {
			setShouldRefetch(true);
		}, 2000); // Set the delay in milliseconds (2000 ms = 2 seconds)

		return () => clearTimeout(timer); // Clean up the timer on component unmount
	}, []);

	const { data: AllLists } = useQuery(
		{
			queryKey: ["Lists"],
			queryFn: () => {
				return apiRequest(
					{
						query: getAllLists,
						variables: {
              // @ts-ignore
							inJurisdiction: userInfo?.executeGetUser[0].Jurisdiction ?? "",
							inScenarioID: scenarioID,
						},
						instance: instance,
						accounts: accounts
					})
			},
      enabled: !!userInfo
		});

	// react query to get progress
	const { data: scenarioProgress } = useQuery(
		{
			queryKey: ["ScenarioProgress"],
			queryFn: () => {
				return apiRequest(
					{
						query: PollScenarioModelProgress,
						variables: {
							inScenarioID: scenarioID,
						},
						instance: instance,
						accounts: accounts
					})
			},
			refetchInterval: shouldRefetch ? 2000 : false, // every 2 seconds
			enabled: shouldRefetch,
			onSuccess: (data) => {
				//@ts-ignore
				const scenarioModelProgress = data?.executePollScenarioModelProgress;
				if (scenarioModelProgress) {
					setPollScenarioProgress({
						isProcessing: scenarioModelProgress[0].IsProcessing === 0 ? false : true,
						scenarioModelRunStatus: scenarioModelProgress[0].ScenarioModelRunStatus ? scenarioModelProgress[0].ScenarioModelRunStatus : scenarioModelRunStatusMessage,
					});
					if (scenarioModelProgress[0].IsProcessing === 0) {
						// Stop refetching when processing is done
						setShouldRefetch(false);
					}
				}
			}
		});



	const { instance, accounts } = useMsal();
	let scenarioTitle = "";
	// @ts-ignore
	client.getQueryData(["scenarioList"])?.executeGetScenarioList.map((item) => {
		if (item.ScenarioID == scenarioID) {
			scenarioTitle = item.ScenarioTitle;
		}
	});
	const [plotQueryProps, setPlotQueryProps] = useState({
		inScenarioID: scenarioID,
    // @ts-ignore
		inState: userInfo?.executeGetUser[0].Jurisdiction !=="IA" ? [userInfo?.executeGetUser[0].Jurisdiction] : ["ALL"],
		inInfraFlag: ["ALL"],
		inSource: "ALL",
		inLocationID: ["ALL"],
		inResourceGroupID: [0],
		inSuperSectorID: 0,
		inProjectTypeID: [0],
		inPLEMGroupID: 0,
		inMasterTypeID: 0,
		unit: 0,
		inDateStart: new Date().getFullYear() + "-07-01"
	});

	const { data: scenarioResult, isFetching: isScenarioFetching } = useQuery(
		["scenarioResult", 'plot', plotQueryProps], {
		queryFn: () => {
			return apiRequest(
				{
					query: GetOverallResult,
					variables: formatQueryParams({
            ...plotQueryProps,
            inDateEnd: new Date().getFullYear() + 16 + "-06-01"}),
					instance: instance,
					accounts: accounts
				})
      },
      enabled: !pollScenarioProgress.isProcessing,
      refetchOnWindowFocus: false,
    }
	);

	const { data: scenarioDetailedResult, isFetching: isScenarioDetailedResultFetching } = useQuery(
		['scenarioDetailedResult', 'plot', plotQueryProps], {
		queryFn: () => {
			return apiRequest(
				{
					query: GetDetailedResult,
					variables: formatQueryParams({
            ...plotQueryProps,
            inDateEnd: new Date().getFullYear() + 5 + "-06-01"}),
					instance: instance,
					accounts: accounts
				})
		},
		enabled: !pollScenarioProgress.isProcessing,
		refetchOnWindowFocus: false,
	}
	)
  // @ts-ignore
  const years = scenarioResult?.executeGetScenarioResult.map((item) => dayjs(item.Month).year());
  const minPlotRange = years?.[0] ?? new Date().getFullYear();
  const maxPlotRange = years?.[years.length - 1] ?? (new Date().getFullYear() + 16);
  const minRange = 1;

  useEffect(() => {
    if (years && years.length > 0) {
      setPlotRange([years[0], years[years.length - 1]]);
    }
  }, [scenarioResult]);

  const handleSliderChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (newValue[1] - newValue[0] < minRange) {
      if (activeThumb === 0) {
        const clamped = Math.min(newValue[0], maxPlotRange - minRange);
        setPlotRange([clamped, clamped + minRange]);
      } else {
        const clamped = Math.max(newValue[1], minRange);
        setPlotRange([clamped - minRange, clamped]);
      }
    } else {
      setPlotRange(newValue as number[]);
    }
  };

  const plotStartIndex = years?.indexOf(plotRange[0]);
  const plotEndIndex = years?.lastIndexOf(plotRange[1]);
  // @ts-ignore
  const slicedResult = scenarioResult?.executeGetScenarioResult.slice(
    plotStartIndex === -1 ? 0 : plotStartIndex,
    plotEndIndex === -1 ? undefined : plotEndIndex + 1
  );

  const monthLabels = slicedResult?.map((item) => dayjs(item.Month).format("DD/MM/YYYY"));
  const dataset1 = slicedResult?.map((item) => plotQueryProps.unit ? item.Sum_Unit : item.Sum_Value);
  const dataset2 = slicedResult?.map((item) => plotQueryProps.unit ? item.Scenario_Unit : item.Scenario_Value);
  // @ts-ignore
  const datasetHeaders = Object.keys(scenarioDetailedResult?.executeGetScenarioDetailResult[0] ?? []);

	// @ts-ignore
	let baselineSum = 0;
	// @ts-ignore
	const projectSet = datasetHeaders.map((key) => scenarioDetailedResult?.executeGetScenarioDetailResult.map((item) => item[key]));
	const [scenarioSum, setScenarioSum] = useState(0);
	useEffect(() => (
		setScenarioSum(projectSet.slice(3, 24).reduce(
			(sum, item) => sum + item.slice(1).reduce(
				(partialSum, a) => partialSum + (Number(a) ? Number(a) : 0), 0)
			, 0))
	), [scenarioDetailedResult])
	const labelList = projectSet.slice(3, 25).map((item) => item[0]);
	const detailedPlotDataset = projectSet.slice(3, 25).map((item, index) => {
		if (projectSet.slice(3)[index][0] != 'Baseline') {
			if (projectSet.slice(3)[index][0]) {
				return {
					type: 'bar' as const,
					label: removeDuplicate(labelList, projectSet.slice(3)[index][0], index),
					data: projectSet.slice(3)[index].slice(1),
					backgroundColor: colorPelletList[index % colorPelletList.length],
				}
			}
		} else {
			baselineSum = projectSet.slice(3)[index].reduce((partialSum, a) => partialSum + (Number(a) ? Number(a) : 0), 0);
			return {
				type: 'line' as const,
				label: projectSet.slice(3)[index][0],
				data: projectSet.slice(3)[index].slice(1),
				backgroundColor: colorPelletList[index % colorPelletList.length],
			}
		}
	}).filter((elem) => {
		return elem !== undefined && elem?.data.reduce((partialSum, a) => partialSum + (Number(a) ? Number(a) : 0), 0) > 0;
	});
	const DetailedData = {
		labels: projectSet[2]?.slice(1),
		datasets: detailedPlotDataset.reverse() ?? [],
	};

	const data = {
		labels: monthLabels,
		datasets: [
			{
				type: "line" as const,
				label: "Baseline Trend",
				data: dataset1,
				borderColor: "#253746",
				backgroundColor: "#253746",
			},
			{
				type: "line" as const,
				label: "Scenario Trend",
				data: dataset2,
				borderColor: "#7e2d40",
				backgroundColor: "#7e2d40",
			}
		],
	};

	// Plot config for detailed stack plot
	const detailTrendOptions = {
		animation: false,
		elements: {
			point: {
				radius: 1
			},
			line: {
				borderColor: '#000000', // baseline line overlay color
				borderWidth: 1,
				fill: false,
			}
		},
		plugins: {
			legend: {
				position: "right" as const,
				reverse: true
			},
			title: {
				display: true,
				text: "Five-year view between baseline and scenario",
				font: {
					size: 20
				},
			},
			tooltip: {
				callbacks: {
					label: function (context) {
						let label = context.dataset.label || '';
						if (label) {
							label += ': ';
						}
						if (context.parsed.y !== null) {
							label += (context.parsed.y / 1000000000).toFixed(2) + "Billion";
						}
						return label;
					},
					afterBody: function (context) {
						let body = Number(Object.values(context[0].parsed._stacks.y).reduce(
							(partialSum: number, a) => partialSum + (Number(a) ? Number(a) : 0), 0
						));
						return "Quarter total: " + (body / 1000000000).toFixed(2) + "Billion";
					}
				}
			}
		},
		scales: {
			x: {
				stacked: true,
				grid: {
					display: false,
					drawTicks: false,
				}
			},
			y: {
				ticks: {
					count: 5,
					callback: function (value) {
						// Checking if the unit is 0 to decide whether to prepend '$'
						var prefix = (plotQueryProps.unit === 0) ? '$ ' : '';

						// Formatting the value and adding commas
						var formattedValue = (value / 1000000).toLocaleString();

						// Concatenating the prefix and formatted value with 'M' suffix
						return prefix + formattedValue + "M";
					}

				},
				stacked: true
			}
		}
	};
	const chartRef = useRef<ChartJS>(null);
	useEffect(() => {
		if (chartRef.current) {
			setScenarioSum(chartRef.current.getSortedVisibleDatasetMetas().slice(1).reduce(
				// @ts-ignore
				(sum, item) => sum + item._parsed.reduce(
					// @ts-ignore
					(partialSum, item) => partialSum + (Number(item.y) ? Number(item.y) : 0), 0), 0));
		}
	}, [chartRef.current?.getSortedVisibleDatasetMetas()]);

  async function startExport() {
    setShowExportConfirmation(false);
    setIsExporting(true);
    setExportProgress(0);
    setTotalMonths(0);
    try {

      const months = monthLabels.map(label => dayjs(label, 'DD/MM/YYYY').format('YYYY-MM-01'));
      setTotalMonths(months.length);
      console.log(plotQueryProps.inState)
      if (plotQueryProps.inState.includes("ALL")) {
        setShowExportWarning(true);
        setIsExporting(false);
        return;
      }

      const results = [];
      for (let i = 0; i < months.length; i++) {
        const month = months[i];
        const variables = { ...plotQueryProps, inMonth: month };

        const result = await apiRequest({
          query: GetDetailedProjectResult,
          variables: formatQueryParams(variables),
          instance: instance,
          accounts: accounts
        });
        // @ts-ignore
        results.push(result);
        setExportProgress(i + 1);
      }
      // @ts-ignore
      const combinedData = results.flatMap(result => result?.executeGetScenarioProjectDetailResult || []);

      if (combinedData.length === 0) {
        console.warn("No data available for the selected criteria to export.");
        // Optionally, show a message to the user
        return;
      }

      const workbook = new Excel.Workbook();
      const worksheet = workbook.addWorksheet("Detailed Project Data");

      worksheet.columns = Object.keys(combinedData[0]).map(key => ({
        header: key,
        key: key,
        width: 20,
      }));

      worksheet.addRows(combinedData);

      const buffer = await workbook.csv.writeBuffer();
      const blob = new Blob([buffer], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "ScenarioDetailedProjectResult.csv");

    } catch (error) {
      console.error("Error exporting data:", error);
      // Optionally, inform the user about the error
    } finally {
      setIsExporting(false);
    }
  }

  function handleOutputResult() {
    setShowExportConfirmation(true);
  }

  return <>
    <ExportDialogs
      showExportWarning={showExportWarning}
      onCloseExportWarning={() => setShowExportWarning(false)}
      showExportConfirmation={showExportConfirmation}
      onCloseExportConfirmation={() => setShowExportConfirmation(false)}
      startExport={startExport}
      isExporting={isExporting}
      plotQueryProps={plotQueryProps}
      monthLabels={monthLabels}
      totalMonths={totalMonths}
      exportProgress={exportProgress}
    />

    {!pollScenarioProgress.isProcessing && !isScenarioFetching && <Grid container spacing={2} alignItems={'center'}>
			<Grid item xs={10}>
				<Box className='tabSelectorBox'>
					{modelFlag && <LinearProgress />}
					<Tabs value={dashTabIndex} onChange={handleDashTabChange} indicatorColor="secondary"
						textColor="inherit"
					>
						<Tab label="Overall trend" {...a11yProps(0)} />
						<Tab label="Five-year view" {...a11yProps(1)} />
					</Tabs>
				</Box>
				<TabPanel value={dashTabIndex} index={0}>
					<Chart type='bar' options={overallTrendOptions(plotQueryProps)} data={data} />
          <Slider
              getAriaLabel={() => "Plot Range"}
              value={plotRange}
              onChange={handleSliderChange}
              valueLabelDisplay="on"
              disableSwap
              step={1}
              marks
              min={minPlotRange}
              max={maxPlotRange}
          />
				</TabPanel>
				<TabPanel value={dashTabIndex} index={1}>
					<Grid container spacing={2} justifyContent='center'>
						<Grid item>
							<Paper elevation={3}>
								<Typography
									variant={"h3"}
									p={1}
								>
									Baseline total:
									$ {(baselineSum / 1000000000).toFixed(2)} Billion
								</Typography>
							</Paper>
						</Grid>
						<Grid item>
							<Paper elevation={3}>
								<Typography
									variant={"h3"}
									p={1}
								>
									Scenario total:
									$ {(scenarioSum / 1000000000).toFixed(2)} Billion
								</Typography>
							</Paper>
						</Grid>
					</Grid>
					{
						// @ts-ignore
						<Chart type='bar' ref={chartRef} options={detailTrendOptions} data={DetailedData} />
					}
				</TabPanel>
			</Grid>
			<Grid item xs={2}>
				<Grid container spacing={1} columns={1}>
					<Grid item xs={1}>
						<Typography variant="h3" align="center">{scenarioTitle.toUpperCase()}</Typography>
					</Grid>
					<PlotFilterList
						AllLists={AllLists}
						plotQueryProps={plotQueryProps}
						setPlotQueryProps={setPlotQueryProps}
						filterConfigList={filterConfigList} />
					<Grid item xs={1}>
						<Tooltip title={"Display Unit Filter"}>
							<ToggleButtonGroup
								value={plotQueryProps.unit}
								exclusive
								onChange={(event, value) => {
									if (value != null) {
										setPlotQueryProps({ ...plotQueryProps, unit: value });
									}
								}
								}
								aria-label="Display Unit Filter"
								fullWidth
								size="small"
							>
								{/* <ToggleButton value={0}>AUD</ToggleButton> */}
								{/* <ToggleButton value={1}>UNIT</ToggleButton> */}
							</ToggleButtonGroup>
						</Tooltip>
					</Grid>
					<Grid item xs={1}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
{/*                  <Button
                     key="backbutton"
                      onClick={() => handleUpdateTab(1)}
                      variant="contained"
                  >
                      <ArrowBackIcon />
                     Back
                  </Button>
*/}
                  <Button
                      key="outputbutton"
                      onClick={handleOutputResult}
                      variant="contained"
                  >
                      <ArrowBackIcon />
                      Export scenario results
                  </Button>
              </Box>
          </Grid>
				</Grid>
			</Grid>
		</Grid>}
    <ProcessExecutionDialog open={pollScenarioProgress.isProcessing} title={pollScenarioProgress.scenarioModelRunStatus} />
    <ProcessExecutionDialog open={!pollScenarioProgress.isProcessing && isScenarioFetching} title={scenarioResultMessage} />
	</>
}
